// src/components/texting/NewTextModal.jsx
// "New text": start a conversation with ANYONE in the database — pipeline
// leads and synced Contacts alike. Picking someone opens a fresh thread in
// the Texting Center; nothing sends until a message is typed there.
import React, { useMemo, useState } from "react";
import { X, MessageSquarePlus } from "lucide-react";
import { toast } from "react-toastify";
import { clientNames } from "../../utils/leadClients";
import { contactPhoneOf, normalizePhoneE164, fmtPhone } from "../../utils/texting";

export default function NewTextModal({ leads, contacts, onPick, onClose }) {
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState("");

  const entries = useMemo(() => {
    const seen = new Set();
    const out = [];
    (leads || [])
      .filter((l) => l.status === "active" && l.contactId)
      .forEach((l) => {
        out.push({
          id: `lead:${l.id}`,
          kind: "Lead",
          name: clientNames(l) || l.contactName || "(unnamed)",
          leadId: l.id,
          contactId: l.contactId,
          stage: l.stage || "",
        });
      });
    (contacts || []).forEach((c) => {
      const phone = normalizePhoneE164(c.phone || "");
      if (!phone || !c.name || seen.has(phone)) return;
      seen.add(phone);
      out.push({
        id: `num:${phone}`,
        kind: "Contact",
        name: c.name,
        phone,
        stage: fmtPhone(phone),
      });
    });
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [leads, contacts]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries.slice(0, 60);
    return entries
      .filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          String(e.phone || "").includes(q.replace(/\D/g, "") || q),
      )
      .slice(0, 60);
  }, [entries, search]);

  const pick = async (e) => {
    setBusyId(e.id);
    try {
      if (e.leadId) {
        const phone = await contactPhoneOf(e.contactId);
        if (!phone) {
          toast.error(`${e.name} has no mobile number on file.`);
          return;
        }
        onPick({
          key: `lead:${e.leadId}`,
          leadId: e.leadId,
          dealId: "",
          contactId: e.contactId,
          name: e.name,
          number: phone,
        });
      } else {
        onPick({
          key: `num:${e.phone}`,
          leadId: "",
          dealId: "",
          contactId: "",
          name: e.name,
          number: e.phone,
        });
      }
      onClose();
    } finally {
      setBusyId("");
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={onClose} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
        <div className="w-full max-w-md max-h-[80vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden pointer-events-auto">
          <div className="bg-[#1E293B] text-white px-5 py-4 flex items-center justify-between">
            <div className="text-base font-bold flex items-center gap-2">
              <MessageSquarePlus size={16} /> New text
            </div>
            <button onClick={onClose} className="p-1 text-slate-300 hover:text-white">
              <X size={18} />
            </button>
          </div>
          <div className="p-4">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search leads and contacts…"
              className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-themeColor"
            />
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-slate-50 px-2 pb-3">
            {shown.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">
                Nobody matches. Try another name or number.
              </p>
            ) : (
              shown.map((e) => (
                <button
                  key={e.id}
                  onClick={() => pick(e)}
                  disabled={!!busyId}
                  className="w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-slate-50 rounded-lg"
                >
                  <span
                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                      e.kind === "Lead"
                        ? "bg-slate-50 text-linkColor"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {e.kind}
                  </span>
                  <span className="text-sm font-medium text-slate-800 flex-1 truncate">
                    {busyId === e.id ? "Opening…" : e.name}
                  </span>
                  <span className="text-[11px] text-slate-400">{e.stage}</span>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}
